import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, NumberCircleOne, NumberCircleTwo, NumberCircleThree, NumberCircleFour } from '@phosphor-icons/react';

const steps = [
  { icon: NumberCircleOne, title: 'Free Consultation', desc: 'Book a free consultation with our admissions team to discuss your goals and eligibility.' },
  { icon: NumberCircleTwo, title: 'College Selection', desc: 'We match you with the best colleges and programs based on your profile and preferences.' },
  { icon: NumberCircleThree, title: 'Exam Prep & Application', desc: 'Get support for exam preparation and complete your application with our expert guidance.' },
  { icon: NumberCircleFour, title: 'Admission & Enrollment', desc: 'We follow up until you receive your admission letter and complete enrollment.' },
];

const benefits = [
  'Personalized college recommendations',
  'Exam preparation for iTEP, Accuplacer, CLBPT/CLBP, IELTS',
  'Full application assistance and review',
  'Document preparation and verification',
  'Scholarship and funding guidance',
  'Post-admission enrollment support',
  'Visa application guidance',
  'Pre-departure orientation',
];

export default function ForStudents() {
  return (
    <div data-testid="for-students-page">
      <section className="relative py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">For Students</p>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6">Your Dream College is Within Reach</h1>
              <p className="text-base lg:text-lg text-gray-600 leading-relaxed mb-8">
                Whether you need help choosing a college, preparing for entrance exams, or completing your application, Study Alliance is here to guide you every step of the way.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/apply" className="inline-flex items-center gap-2 bg-blue-800 text-white px-6 py-3 rounded-md font-medium hover:bg-blue-700 transition-all hover:-translate-y-0.5" data-testid="student-apply-btn">
                  Apply Now <ArrowRight size={18} />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-all" data-testid="student-consult-btn">
                  Book Free Consultation
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6">
              <img src="https://images.unsplash.com/photo-1513258496099-48168024aec0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxzdHVkZW50JTIwc3R1ZHlpbmd8ZW58MHx8fHwxNzc2MzY1NTY0fDA&ixlib=rb-4.1.0&q=85" alt="Students" className="rounded-lg shadow-lg w-full object-cover max-h-[440px]" />
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">How It Works</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Simple Steps to Your Admission</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((s, i) => (
              <div key={s.title} className="relative p-6 bg-white border border-gray-200 rounded-lg">
                <s.icon size={36} weight="fill" className="text-blue-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 -right-4 text-gray-300">
                    <ArrowRight size={20} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">Why Study Alliance</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-6">What You Get</h2>
              <p className="text-gray-600 leading-relaxed">
                Our comprehensive support covers every aspect of the college admission process, from initial consultation to enrollment and beyond.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {benefits.map(b => (
                  <div key={b} className="flex items-start gap-3 p-3">
                    <CheckCircle size={20} weight="fill" className="text-blue-500 mt-0.5 shrink-0" />
                    <span className="text-gray-700 text-sm">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-800 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white tracking-tight mb-4">Start Your Application Today</h2>
          <p className="text-gray-300 mb-8">Join thousands of students who have successfully achieved their education goals with Study Alliance.</p>
          <Link to="/apply" className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-md font-medium hover:bg-blue-700 transition-all" data-testid="student-cta-btn">
            Get Started <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
